import { useCallback, useEffect, useRef, useState } from "react"
import type { DragEvent, FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import { uploadFileWithProgress, formatBytes } from "../../lib/uploadWithProgress"
import { enqueueTranscription } from "../../lib/transcriptionQueue"
import {
  createQueueItems,
  patchItem,
  removeItem,
  isQueueActive,
  nextPending,
  runQueue,
  type QueueItem,
  type UploadCallbacks,
} from "../../lib/videoUploadQueue"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { textareaCls, labelCls, inputCls, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import VideoPreviewPlayer from "../../components/admin/VideoPreviewPlayer"
import AudioTracksPanel from "../../components/admin/AudioTracksPanel"
import TranscriptPanel from "../../components/admin/TranscriptPanel"
import { useVideoStatusSocket } from "../../hooks/useVideoStatusSocket"
import { VIDEO_STATUS_LABELS, type VideoStatus } from "../../types/video"
import { VideoIcon, CloseIcon, CheckCircleIcon, RefreshIcon, AlertIcon } from "../../icons"
import { LoadState } from "@/components/ui/LoadState"

type EditTab = "details" | "audio" | "transcript"

type Video = {
  id: string
  title: string
  description?: string | null
  status: VideoStatus
  duration?: number
  error_message?: string | null
  transcript_text?: string | null
}

const QUEUE_STATUS_LABELS: Record<QueueItem["status"], string> = {
  pending: "En espera",
  uploading: "Subiendo",
  processing: "Procesando",
  done: "Listo",
  error: "Error",
}

const MAX_TITLE_LENGTH = 255

function titleFromFileName(fileName: string): string {
  const withoutExt = fileName.replace(/\.[^./\\]+$/, "")
  return withoutExt.slice(0, MAX_TITLE_LENGTH) || fileName.slice(0, MAX_TITLE_LENGTH)
}

function formatDuration(seconds?: number): string {
  if (!seconds) return "—"
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, "0")}`
}

function UploadCloudIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 18a4 4 0 01-.5-7.97A5.5 5.5 0 0117.5 9.5 4 4 0 0117 18H7z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M12 11v6M12 11l-2.5 2.5M12 11l2.5 2.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const STEPS = [
  { n: 1 as const, label: "Solicitar URL" },
  { n: 2 as const, label: "Subiendo a R2" },
  { n: 3 as const, label: "Procesando" },
]

function UploadStepper({ stage, percent }: { stage: 1 | 2 | 3; percent: number | null }) {
  return (
    <div className="flex items-start justify-between">
      {/* Sin el porcentaje aquí a propósito: anunciarlo en cada tick saturaría
          a un usuario de lector de pantalla. Solo se anuncian cambios de etapa. */}
      <span role="status" aria-live="polite" className="sr-only">
        {STEPS[stage - 1].label}
      </span>
      {STEPS.map((step, i) => {
        const done = stage > step.n
        const active = stage === step.n
        return (
          <div key={step.n} className="relative flex flex-1 flex-col items-center gap-2 text-center">
            {i > 0 && (
              <span
                className={`absolute top-5 h-0.5 ${done || active ? "bg-brand-500" : "bg-gray-200 dark:bg-gray-700"}`}
                style={{ left: "calc(-50% + 20px)", width: "calc(100% - 40px)" }}
              />
            )}
            <div className="relative z-10 size-10 shrink-0">
              {done ? (
                <div className="flex size-10 items-center justify-center rounded-full bg-success-50 dark:bg-success-500/10">
                  <CheckCircleIcon className="size-5" />
                </div>
              ) : active ? (
                <div
                  className="flex size-10 items-center justify-center rounded-full"
                  style={{ background: `conic-gradient(var(--color-brand-500) ${percent ?? 0}%, var(--color-gray-200) 0)` }}
                >
                  <div className="flex size-8 items-center justify-center rounded-full bg-white text-[11px] font-bold text-brand-600 dark:bg-gray-900">
                    {percent !== null ? `${percent}%` : "…"}
                  </div>
                </div>
              ) : (
                <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500 dark:text-gray-400 dark:bg-gray-800">
                  {step.n}
                </div>
              )}
            </div>
            <p className={`text-[13px] font-medium ${active || done ? "text-gray-800 dark:text-white/90" : "text-gray-400"}`}>
              {step.label}
            </p>
          </div>
        )
      })}
    </div>
  )
}

const PIPELINE_STAGES: { key: VideoStatus; label: string }[] = [
  { key: "uploading", label: "Subiendo" },
  { key: "processing", label: "Procesando" },
  { key: "transcribing", label: "Transcribiendo" },
  { key: "ready", label: "Listo" },
]

type StageState = "done" | "active" | "pending" | "failed"

// Las fallas de transcripción nunca bloquean el video (nunca llegan a
// status: 'error') — si el video está en error, siempre fue durante
// "Procesando". Ver openspec/changes/academy-video-server-transcription.
function stageStateFor(status: VideoStatus, stageKey: VideoStatus): StageState {
  const order: VideoStatus[] = ["uploading", "processing", "transcribing", "ready"]
  if (status === "error") {
    if (stageKey === "processing") return "failed"
    return stageKey === "uploading" ? "done" : "pending"
  }
  const statusIndex = order.indexOf(status)
  const stageIndex = order.indexOf(stageKey)
  if (stageIndex < statusIndex) return "done"
  if (stageIndex === statusIndex) return "active"
  return "pending"
}

function PipelineStepper({ status }: { status: VideoStatus }) {
  return (
    <div className="flex items-center rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-4 dark:border-gray-700 dark:bg-white/[0.02]">
      <span role="status" aria-live="polite" className="sr-only">
        Estado del video: {VIDEO_STATUS_LABELS[status]}
      </span>
      {PIPELINE_STAGES.map((stage, i) => {
        const state = stageStateFor(status, stage.key)
        return (
          <div key={stage.key} className="flex flex-1 items-center last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`flex size-7 shrink-0 items-center justify-center rounded-full ${
                  state === "done"
                    ? "bg-success-500"
                    : state === "failed"
                    ? "bg-error-500"
                    : state === "active"
                    ? "bg-warning-100 ring-2 ring-warning-500 dark:bg-warning-500/15"
                    : "bg-gray-100 dark:bg-gray-800"
                }`}
              >
                {state === "done" && <CheckCircleIcon className="size-3.5 text-white" />}
                {state === "failed" && <CloseIcon className="size-3.5 text-white" />}
                {state === "active" && <span className="size-2 animate-pulse rounded-full bg-warning-500" />}
                {state === "pending" && <span className="size-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />}
              </div>
              <span
                className={`text-[11px] font-medium ${
                  state === "done"
                    ? "text-success-600"
                    : state === "failed"
                    ? "text-error-600"
                    : state === "active"
                    ? "text-warning-600"
                    : "text-gray-400"
                }`}
              >
                {stage.label}
              </span>
            </div>
            {i < PIPELINE_STAGES.length - 1 && (
              <div className={`mx-1.5 h-0.5 flex-1 rounded-full ${state === "done" ? "bg-success-500" : "bg-gray-200 dark:bg-gray-700"}`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

function Dropzone({
  file,
  isDragActive,
  disabled,
  multiple,
  onPick,
  onDragOver,
  onDragLeave,
  onDrop,
  fileInputRef,
  onChangeFiles,
  onClear,
}: {
  file: File | null
  isDragActive: boolean
  disabled?: boolean
  multiple?: boolean
  onPick: () => void
  onDragOver: (e: DragEvent<HTMLDivElement>) => void
  onDragLeave: () => void
  onDrop: (e: DragEvent<HTMLDivElement>) => void
  fileInputRef: React.RefObject<HTMLInputElement | null>
  onChangeFiles: (files: File[]) => void
  onClear: () => void
}) {
  return (
    <div className="shrink-0">
      {file ? (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-white/[0.02]">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-500 dark:bg-brand-500/10">
            <VideoIcon className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">{file.name}</p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center gap-1 font-medium text-success-600">
                <CheckCircleIcon className="size-3" />
                Formato válido
              </span>
              · {formatBytes(file.size)}
            </p>
          </div>
          {!disabled && (
            <div className="flex shrink-0 items-center gap-3">
              <button type="button" onClick={onPick} className="text-xs font-medium text-brand-600 hover:text-brand-700">
                Cambiar
              </button>
              <button
                type="button"
                onClick={onClear}
                aria-label="Quitar archivo"
                className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
              >
                <CloseIcon className="size-3.5" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={onPick}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onPick()
          }}
          className={`flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
            isDragActive
              ? "border-brand-200 bg-brand-50 shadow-[0_0_0_4px_rgba(70,95,255,0.12)] dark:border-brand-500/40 dark:bg-brand-500/10"
              : "border-gray-300 bg-gray-50/60 hover:border-gray-400 dark:border-gray-700 dark:bg-white/[0.02]"
          }`}
        >
          <div
            className={`flex size-14 items-center justify-center rounded-full ${
              isDragActive ? "bg-brand-100 text-brand-600 dark:bg-brand-500/20" : "bg-brand-50 text-brand-500 dark:bg-brand-500/10"
            }`}
          >
            <UploadCloudIcon className="size-6" />
          </div>
          <p className={`text-sm font-medium ${isDragActive ? "text-brand-600" : "text-gray-700 dark:text-gray-300"}`}>
            {isDragActive ? "Suelta el archivo para subirlo" : multiple ? "Arrastra tus videos aquí" : "Arrastra tu video aquí"}
          </p>
          {!isDragActive && <p className="text-xs text-gray-500 dark:text-gray-400">o haz clic para buscar en tu equipo{multiple ? " (puedes elegir varios)" : ""}</p>}
          <div className="mt-1 flex gap-1.5">
            <span className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-900">
              MP4 · MOV · WEBM
            </span>
            <span className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-900">
              Máx. 5 GB
            </span>
          </div>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        multiple={multiple}
        onChange={(e) => onChangeFiles(e.target.files ? Array.from(e.target.files) : [])}
        className="hidden"
      />
    </div>
  )
}

function QueueItemProgress({ item }: { item: QueueItem }) {
  if (item.status === "uploading" && item.progress !== null) {
    return (
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${item.progress}%` }} />
      </div>
    )
  }
  return null
}

function QueueItemBadge({ status }: { status: QueueItem["status"] }) {
  const cls =
    status === "done"
      ? "bg-success-50 text-success-600 dark:bg-success-500/10"
      : status === "error"
      ? "bg-error-50 text-error-600 dark:bg-error-500/10"
      : status === "uploading" || status === "processing"
      ? "bg-warning-50 text-warning-700 dark:bg-warning-500/10"
      : "bg-gray-100 text-gray-500 dark:bg-gray-800"
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {status === "uploading" || status === "processing" ? <RefreshIcon className="size-3 animate-spin" /> : null}
      {status === "done" ? <CheckCircleIcon className="size-3" /> : null}
      {status === "error" ? <AlertIcon className="size-3" /> : null}
      {QUEUE_STATUS_LABELS[status]}
    </span>
  )
}

function UploadQueueList({
  queue,
  onRetry,
  onRemove,
}: {
  queue: QueueItem[]
  onRetry: (id: string) => void
  onRemove: (id: string) => void
}) {
  if (queue.length === 0) {
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 text-gray-400 dark:border-gray-700 dark:bg-white/[0.02]">
        <VideoIcon className="size-8" />
        <p className="text-xs">Selecciona uno o más videos para empezar</p>
      </div>
    )
  }

  // Deliberadamente sin el porcentaje de progreso aquí: anunciarlo en cada
  // tick saturaría a un usuario de lector de pantalla. Solo se anuncian
  // transiciones de estado (pendiente → subiendo → listo/error).
  const summary = queue.map((item) => `${item.file.name}: ${QUEUE_STATUS_LABELS[item.status]}`).join(". ")

  return (
    <div className="flex max-h-[420px] flex-col gap-2 overflow-y-auto pr-1">
      <span role="status" aria-live="polite" className="sr-only">
        {summary}
      </span>
      {queue.map((item) => (
        <div
          key={item.id}
          className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-700 dark:bg-white/[0.02]"
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-500 dark:bg-brand-500/10">
            <VideoIcon className="size-4.5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">{item.file.name}</p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              {formatBytes(item.file.size)}
              {item.status === "uploading" && item.progress !== null ? ` · ${item.progress}%` : null}
              {item.status === "error" && item.error ? ` · ${item.error}` : null}
            </p>
            <QueueItemProgress item={item} />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <QueueItemBadge status={item.status} />
            {item.status === "error" && (
              <button
                type="button"
                onClick={() => onRetry(item.id)}
                className="text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                Reintentar
              </button>
            )}
            {(item.status === "pending" || item.status === "error") && (
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                aria-label="Quitar de la cola"
                className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
              >
                <CloseIcon className="size-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function VideoFormPage() {
  const { id } = useParams<{ id: string }>()
  const mode: "create" | "edit" = id ? "edit" : "create"
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const xhrRef = useRef<XMLHttpRequest | null>(null)
  const queueXhrRefsRef = useRef<Map<string, { current: XMLHttpRequest | null }>>(new Map())
  const queueRef = useRef<QueueItem[]>([])

  const [video, setVideo] = useState<Video | null>(null)
  const [loading, setLoading] = useState(mode === "edit")
  const [form, setForm] = useState({ title: "", description: "" })
  // `file` is only used by the edit-mode "replace file" flow (still single-file
  // by design — it replaces the one file of one existing video). Create-mode
  // uses `queue` instead, to support multiple videos at once.
  const [file, setFile] = useState<File | null>(null)
  const [queue, setQueueDisplay] = useState<QueueItem[]>([])
  const [replacingFile, setReplacingFile] = useState(mode === "create")
  const [isDragActive, setIsDragActive] = useState(false)
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [activeTab, setActiveTab] = useState<EditTab>("details")
  const [uploadStage, setUploadStage] = useState<1 | 2 | 3>(1)
  const [uploadPercent, setUploadPercent] = useState<number | null>(null)

  function updateQueue(updater: (prev: QueueItem[]) => QueueItem[]) {
    queueRef.current = updater(queueRef.current)
    setQueueDisplay(queueRef.current)
  }

  useEffect(() => {
    if (mode !== "edit" || !id) return
    setLoading(true)
    api
      .get(`/academy/admin/videos/${id}`)
      .then((res) => {
        const v: Video = res.data?.data
        setVideo(v)
        setForm({ title: v.title, description: v.description ?? "" })
      })
      .catch((err) => toastError(err))
      .finally(() => setLoading(false))
  }, [mode, id])

  const refreshStatus = useCallback(() => {
    if (!id) return
    api
      .get(`/academy/admin/videos/${id}`)
      .then((res) => {
        const v: Video = res.data?.data
        setVideo((prev) => (prev ? { ...prev, status: v.status, error_message: v.error_message, duration: v.duration } : v))
      })
      .catch(() => {})
  }, [id])

  // Ver el progreso del pipeline sin salir de la pantalla — vía el mismo
  // WebSocket que VideosManager.tsx (VideoStatusHubDO), no polling. Nunca
  // pisa form.title/form.description mientras el admin los edita. El
  // fallback solo actúa si el WS nunca logra conectar (ver
  // useVideoStatusSocket) y reusa el mismo GET puntual que refreshStatus.
  useVideoStatusSocket({
    onEvent: (event) => {
      if (mode !== "edit" || event.videoId !== id) return
      setVideo((prev) =>
        prev ? { ...prev, status: event.status, error_message: event.errorMessage, duration: event.duration ?? prev.duration } : prev
      )
      if (event.status === "ready") {
        toastSuccess("El video terminó de procesarse y ya está listo.")
      } else if (event.status === "error") {
        toastError(event.errorMessage ?? "El video falló al procesarse.")
      }
    },
    onFallbackPoll: () => {
      if (mode === "edit" && video && video.status !== "ready" && video.status !== "error") {
        refreshStatus()
      }
    },
  })

  function goBack() {
    navigate("/instructor/videos")
  }

  function onDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(true)
  }
  function onDragLeave() {
    setIsDragActive(false)
  }
  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragActive(false)
    if (mode === "create") {
      handleFilesSelected(Array.from(e.dataTransfer.files ?? []))
    } else {
      setFile(e.dataTransfer.files?.[0] ?? null)
    }
  }
  function handleCancelUpload() {
    if (mode === "create") {
      const active = queueRef.current.find((i) => i.status === "uploading")
      if (active) queueXhrRefsRef.current.get(active.id)?.current?.abort()
    } else {
      xhrRef.current?.abort()
    }
  }

  function handleFilesSelected(files: File[]) {
    if (files.length === 0) return
    updateQueue((q) => [...q, ...createQueueItems(files, titleFromFileName)])
  }

  async function uploadQueueItem(item: QueueItem, callbacks: UploadCallbacks): Promise<{ videoId: string }> {
    // Resume: the file already reached R2 last time and only triggering
    // processing failed — retry just that step instead of re-uploading.
    if (item.videoId && item.uploadedToR2) {
      callbacks.onProcessing()
      await api.post(`/academy/admin/videos/${item.videoId}/process`)
      return { videoId: item.videoId }
    }

    const uploadReqRes = await api.post("/academy/admin/videos/upload", {
      title: item.title,
      description: form.description || undefined,
      file_name: item.file.name,
      file_size: item.file.size,
      content_type: item.file.type,
    })
    const { video_id, original_track_id, upload_url, upload_headers } = uploadReqRes.data.data
    updateQueue((q) => patchItem(q, item.id, { videoId: video_id }))

    const itemXhrRef: { current: XMLHttpRequest | null } = { current: null }
    queueXhrRefsRef.current.set(item.id, itemXhrRef)
    let fileUploaded = false
    try {
      await uploadFileWithProgress(upload_url, upload_headers, item.file, callbacks.onProgress, itemXhrRef)
      fileUploaded = true
      updateQueue((q) => patchItem(q, item.id, { uploadedToR2: true }))

      callbacks.onProcessing()
      await api.post(`/academy/admin/videos/${video_id}/process`)

      if (original_track_id) {
        enqueueTranscription({ videoId: video_id, trackId: original_track_id, file: item.file })
      }
      return { videoId: video_id }
    } catch (err) {
      // El archivo nunca llegó a R2: el video creado arriba es un stub muerto,
      // lo limpiamos para no dejar la lección "tomada" para siempre. Si el
      // archivo SÍ llegó, dejamos la fila aunque falle el disparo de
      // procesamiento (se puede reintentar sin volver a subir el archivo).
      if (!fileUploaded) {
        await api.delete(`/academy/admin/videos/${video_id}`).catch(() => {})
      }
      const aborted = err instanceof Error && err.message.includes("aborted")
      throw aborted ? new Error("Subida cancelada.") : err
    } finally {
      queueXhrRefsRef.current.delete(item.id)
    }
  }

  async function maybeRunQueue() {
    if (isQueueActive(queueRef.current) || !nextPending(queueRef.current)) return
    setBusy(true)
    try {
      await runQueue(() => queueRef.current, updateQueue, uploadQueueItem)
    } finally {
      setBusy(false)
      if (queueRef.current.length > 0 && queueRef.current.every((item) => item.status === "done")) {
        goBack()
      }
    }
  }

  async function handleCreateUpload(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (queue.length === 0) {
      toastError("Selecciona al menos un archivo de video.")
      return
    }
    await maybeRunQueue()
  }

  function handleRetryQueueItem(itemId: string) {
    updateQueue((q) => patchItem(q, itemId, { status: "pending", error: undefined }))
    void maybeRunQueue()
  }

  function handleRemoveQueueItem(itemId: string) {
    updateQueue((q) => removeItem(q, itemId))
  }

  async function handleReplaceFile() {
    if (!file || !id) {
      toastError("Selecciona un archivo de video.")
      return
    }
    setBusy(true)
    setUploadStage(1)
    try {
      const res = await api.post(`/academy/admin/videos/${id}/replace-file`, { content_type: file.type })
      const { upload_url, upload_headers } = res.data.data

      setUploadStage(2)
      setUploadPercent(0)
      await uploadFileWithProgress(upload_url, upload_headers, file, setUploadPercent, xhrRef)

      setUploadStage(3)
      setUploadPercent(null)
      await api.post(`/academy/admin/videos/${id}/process`)

      toastSuccess("Archivo reemplazado. El video se está reprocesando.")
      setFile(null)
      setReplacingFile(false)
      refreshStatus()
    } catch (err) {
      const aborted = err instanceof Error && err.message.includes("aborted")
      toastError(aborted ? "Subida cancelada." : err)
      setUploadPercent(null)
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveMetadata(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!id) return
    setSaving(true)
    try {
      await api.put(`/academy/admin/videos/${id}`, {
        title: form.title,
        description: form.description || null,
      })
      toastSuccess("Video actualizado.")
      goBack()
    } catch (err) {
      toastError(err)
    } finally {
      setSaving(false)
    }
  }

  async function handleRetry() {
    if (!id) return
    setRetrying(true)
    try {
      await api.post(`/academy/admin/videos/${id}/process`)
      toastSuccess("Reprocesamiento encolado.")
      refreshStatus()
    } catch (err) {
      toastError(err)
    } finally {
      setRetrying(false)
    }
  }

  const formId = mode === "create" ? "video-create-form" : "video-edit-form"
  const canSubmitCreate = mode === "create" && queue.length > 0
  const canSubmitEdit = mode === "edit" && !!form.title.trim()

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Contenido",
        { label: "Videos", to: "/instructor/videos" },
        mode === "create" ? "Subir video" : "Editar video",
      ]}
      title={mode === "create" ? "Subir video" : "Editar video"}
      subtitle={mode === "create" ? "Sube uno o más videos nuevos al catálogo" : "Actualiza el título, la descripción o el archivo del video"}
      tabs={[]}
      metrics={[]}
      actions={
        <>
          <button type="button" onClick={goBack} disabled={busy || saving} className={`min-h-11 ${btnSecondary} disabled:opacity-50`}>
            Cancelar
          </button>
          {mode === "create" ? (
            <button
              form={formId}
              type="submit"
              disabled={busy || !canSubmitCreate}
              className={`min-h-11 ${btnSave} inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {busy ? "Subiendo..." : queue.length > 1 ? `Subir ${queue.length} videos` : "Subir video"}
            </button>
          ) : (
            <button
              form={formId}
              type="submit"
              disabled={saving || loading || !canSubmitEdit}
              className={`min-h-11 ${btnSave} inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </button>
          )}
        </>
      }
    >
      {mode === "edit" && loading ? (
        <LoadState />
      ) : mode === "edit" && !video ? (
        <p role="alert" aria-live="assertive" className="text-sm text-error-600 dark:text-error-400">Video no encontrado.</p>
      ) : (
        <>
          {mode === "edit" && (
            <div role="tablist" className="mb-4 flex gap-1 rounded-xl border border-gray-200 bg-white p-1 dark:border-gray-800 dark:bg-white/[0.03]">
              {(["details", "audio", "transcript"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                  className={`min-h-11 rounded-lg px-3 py-1.5 text-sm font-medium ${
                    activeTab === tab
                      ? "bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.04]"
                  }`}
                >
                  {tab === "details" ? "Detalles" : tab === "audio" ? "Audio" : "Transcripción"}
                </button>
              ))}
            </div>
          )}

          {(mode === "create" || activeTab === "details") && (
            <form id={formId} onSubmit={mode === "create" ? handleCreateUpload : handleSaveMetadata}>
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="flex h-full flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                    {mode === "create" ? "Archivos" : "Detalles"}
                  </h3>

                  {mode === "create" ? (
                    <Dropzone
                      file={null}
                      isDragActive={isDragActive}
                      disabled={busy}
                      multiple
                      onPick={() => fileInputRef.current?.click()}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      onDrop={onDrop}
                      fileInputRef={fileInputRef}
                      onChangeFiles={handleFilesSelected}
                      onClear={() => {}}
                    />
                  ) : (
                    <div>
                      <label className={labelCls}>Título</label>
                      <input
                        disabled={saving}
                        value={form.title}
                        onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                        className={`${inputCls} disabled:opacity-60`}
                      />
                    </div>
                  )}

                  <div className="flex min-h-0 flex-1 flex-col">
                    <label className={labelCls}>
                      Descripción <span className="font-normal text-gray-400">(opcional)</span>
                    </label>
                    <textarea
                      disabled={mode === "create" ? busy : saving}
                      placeholder={
                        mode === "create"
                          ? "Agrega notas para el equipo — se aplican a todos los videos de esta subida"
                          : "Agrega notas para el equipo, por ejemplo qué cubre la clase"
                      }
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      className={`${textareaCls} min-h-[96px] flex-1 resize-none disabled:opacity-60`}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                      {mode === "create" ? "Cola de subida" : "Archivo"}
                    </h3>
                    {mode === "edit" && !replacingFile && (
                      <button
                        type="button"
                        onClick={() => setReplacingFile(true)}
                        className={`${btnSecondary} inline-flex min-h-11 items-center gap-1.5 px-3 text-xs`}
                      >
                        <RefreshIcon className="size-3.5" />
                        Reemplazar archivo
                      </button>
                    )}
                  </div>

                  {mode === "create" ? (
                    <UploadQueueList queue={queue} onRetry={handleRetryQueueItem} onRemove={handleRemoveQueueItem} />
                  ) : replacingFile ? (
                    <>
                      <Dropzone
                        file={file}
                        isDragActive={isDragActive}
                        disabled={busy}
                        onPick={() => fileInputRef.current?.click()}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        fileInputRef={fileInputRef}
                        onChangeFiles={(files) => setFile(files[0] ?? null)}
                        onClear={() => setFile(null)}
                      />
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        No pierdes el título, la descripción ni la lección asociada al reemplazar el archivo.
                      </p>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setReplacingFile(false)
                            setFile(null)
                          }}
                          disabled={busy}
                          className={`min-h-11 ${btnSecondary} px-3 text-xs disabled:opacity-50`}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleReplaceFile}
                          disabled={busy || !file}
                          className={`min-h-11 ${btnSave} px-3 text-xs disabled:cursor-not-allowed disabled:opacity-50`}
                        >
                          {busy ? "Subiendo..." : "Subir archivo"}
                        </button>
                      </div>
                    </>
                  ) : video ? (
                    <>
                      {video.status !== "ready" && <PipelineStepper status={video.status} />}

                      <VideoPreviewPlayer videoId={video.id} status={video.status} />
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span className="inline-flex items-center gap-1.5">
                          {video.status === "ready" && <CheckCircleIcon className="size-3.5 text-success-500" />}
                          Estado: {VIDEO_STATUS_LABELS[video.status]}
                        </span>
                        <span>Duración: {formatDuration(video.duration)}</span>
                      </div>

                      {(video.status === "uploading" || video.status === "processing" || video.status === "transcribing") && (
                        <div
                          role="status"
                          aria-live="polite"
                          className="flex items-center gap-3 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2.5 dark:border-warning-500/30 dark:bg-warning-500/10"
                        >
                          <RefreshIcon className="size-4 shrink-0 animate-spin text-warning-600" />
                          <div className="min-w-0 flex-1">
                            <p className="flex items-center gap-1.5 text-xs font-medium text-warning-700 dark:text-warning-400">
                              <span className="size-1.5 animate-pulse rounded-full bg-warning-500" />
                              En vivo
                            </p>
                            <p className="text-[11px] text-warning-700/80 dark:text-warning-400/80">
                              Esta pantalla se actualiza sola mientras se procesa — no necesitas recargar.
                            </p>
                          </div>
                        </div>
                      )}

                      {video.status === "error" && (
                        <div role="alert" aria-live="assertive" className="flex flex-col gap-3 rounded-xl bg-error-50 p-4 dark:bg-error-500/10">
                          <div className="flex gap-3">
                            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-error-600 dark:bg-gray-900">
                              <AlertIcon className="size-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold text-gray-800 dark:text-white/90">No pudimos procesar este video</p>
                              {video.error_message && (
                                <p className="mt-1 text-xs text-error-700 dark:text-error-400">{video.error_message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-start gap-1.5 border-t border-error-200/60 pt-3 text-[11px] text-gray-500 dark:border-error-500/20 dark:text-gray-400">
                            <CheckCircleIcon className="mt-0.5 size-3.5 shrink-0 text-success-500" />
                            Tu título, descripción y archivo original siguen intactos — reintentar no borra nada.
                          </div>
                          <button
                            type="button"
                            onClick={handleRetry}
                            disabled={retrying}
                            className={`min-h-11 ${btnSave} inline-flex w-fit items-center gap-1.5 text-xs disabled:cursor-not-allowed`}
                          >
                            <RefreshIcon className="size-3.5" />
                            {retrying ? "Reintentando..." : "Reintentar procesamiento"}
                          </button>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              </div>

              {mode === "create" && busy ? (
                <div className="mt-5 flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">No cierres esta pestaña mientras se suben los videos.</span>
                  <button
                    type="button"
                    onClick={handleCancelUpload}
                    className={`min-h-11 ${btnSecondary} px-3 text-xs`}
                  >
                    Cancelar subida actual
                  </button>
                </div>
              ) : null}

              {mode === "edit" && busy ? (
                <div className="mt-5 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                  <h3 className="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Progreso del reemplazo</h3>
                  <UploadStepper stage={uploadStage} percent={uploadPercent} />
                  <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4 dark:border-gray-800">
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">No cierres esta pestaña mientras se sube el video.</span>
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      disabled={uploadStage !== 2}
                      className={`min-h-11 ${btnSecondary} px-3 text-xs disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                      Cancelar subida
                    </button>
                  </div>
                </div>
              ) : null}
            </form>
          )}

          {mode === "edit" && video && activeTab === "audio" && (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <h3 className="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Tracks de audio por idioma</h3>
                <AudioTracksPanel videoId={video.id} />
              </div>
              <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Vista previa</h3>
                <VideoPreviewPlayer videoId={video.id} status={video.status} />
              </div>
            </div>
          )}

          {mode === "edit" && video && activeTab === "transcript" && (
            <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
              <h3 className="mb-4 text-sm font-semibold text-gray-800 dark:text-white/90">Transcripción</h3>
              <TranscriptPanel videoId={video.id} status={video.status} initialText={video.transcript_text ?? null} />
            </div>
          )}
        </>
      )}
    </ModuleLayout>
  )
}
